// src/pages/PatientProfile/components/CreateCaseModal.jsx
import React, { useEffect, useState } from 'react';
import { X, AlertCircle } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { createCasePhasesFromTemplates } from '../../../lib/createCasePhases';
import {
  buildDefaultCasesPrescriptionFields,
  seedDefaultCasePrescription,
} from '../../../lib/defaultCasePrescription';
import { patientAllowsNewCase } from '../../../lib/patientStatus';

import { isActiveCase } from '../../../lib/activeCaseRules';

const CreateCaseModal = ({ isOpen, onClose, patient, currentUserId }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    archToTreat: '',
    treatmentType: '',
    caseType: 'aligners',
    priority: 'normal',
    questionsNotes: '',
    clinicalNotes: ''
  });
 let selectedPatient = patient; // pour plus de clarté dans le code

  useEffect(() => {
    setError(null);
  }, [patient?.id]);

  if (!isOpen) return null;

const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedPatient) {
      setError('Veuillez sélectionner un patient');
      return;
    }

    if (!patientAllowsNewCase(selectedPatient)) {
      setError(
        'Ce patient est inactif. Réactivez le dossier patient pour créer une nouvelle conception.'
      );
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const { data: livePatient, error: liveErr } = await supabase
        .from('patients')
        .select('id, status')
        .eq('id', selectedPatient.id)
        .single();

      if (liveErr) throw liveErr;
      if (!patientAllowsNewCase({ status: livePatient?.status })) {
        setError(
          'Ce patient est inactif. Réactivez le dossier patient pour créer une nouvelle conception.'
        );
        return;
      }

      // Bloquer les doublons actifs pour le même couple dentiste + patient.
      const { data: existingCases, error: existingCasesError } = await supabase
        .from('cases')
        .select('id, status, current_phase')
        .eq('patient_id', selectedPatient.id)
        .eq('user_profile_id', currentUserId);

      if (existingCasesError) throw existingCasesError;

      const hasActiveCase = (existingCases || []).some(isActiveCase);

      if (hasActiveCase) {
        setError(
          'Une conception est déjà en cours pour ce patient. La création est autorisée uniquement après expédition terminée (status completed et phase delivered).'
        );
        return;
      }

      const defaultPrescription = buildDefaultCasesPrescriptionFields();

      // 1. Créer le cas principal
      const { data: caseData, error: caseError } = await supabase
        .from('cases')
        .insert([
          {
            patient_id: selectedPatient.id,
            user_profile_id: currentUserId,
            title: formData.title || `Cas ${selectedPatient.name}`,
            status: 'in_progress',
            priority: formData.priority,
            current_phase: 'submission',
            arch_to_treat: formData.archToTreat || defaultPrescription.arch_to_treat,
            treatment_type: formData.treatmentType || defaultPrescription.treatment_type,
            case_type: formData.caseType,
            questions_notes: formData.questionsNotes || null,
            clinical_notes: formData.clinicalNotes || null,
            created_date: new Date().toISOString(),
            start_date: null,
            due_date: null
          }
        ])
        .select()
        .single();

      if (caseError) throw caseError;

      await seedDefaultCasePrescription(supabase, caseData.id);

      // 2. Créer les phases + jalons depuis les templates (dynamique)
      const { error: templateError } = await createCasePhasesFromTemplates(caseData.id, currentUserId);
      if (templateError) throw templateError;

      // 3. Fermer tous les autres cas du patient (sauf le nouveau)
      const { error: closeError } = await supabase
        .from('cases')
        .update({ status: 'draft' })
        .eq('patient_id', selectedPatient.id)
        .neq('id', caseData.id)
        .neq('status', 'draft'); // éviter les mises à jour inutiles

      if (closeError) throw closeError;

      // 4. Incrémenter le compteur de cas actifs du patient
      await supabase
        .from('patients')
        .update({ 
          active_cases: 1, // seul le nouveau cas est actif désormais
          last_visit: new Date().toISOString()
        })
        .eq('id', selectedPatient.id);

      // 5. Décrémenter current_case_number de l'utilisateur
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('current_case_number')
        .eq('id', currentUserId)
        .single();

      if (userProfile) {
        const newCaseNumber = Math.max(0, parseInt(userProfile.current_case_number || 0) - 1);
        await supabase
          .from('user_profiles')
          .update({ current_case_number: newCaseNumber.toString() })
          .eq('id', currentUserId);
      }

      // Succès - fermer le modal et rediriger (onglet Fichiers ouvert pour upload)
      onClose();
      window.location.href = `/case-detail/${caseData.id}?tab=files`;

    } catch (err) {
      console.error('Error creating case:', err);
      setError(err.message || 'Une erreur est survenue lors de la création du cas');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              Créer une Nouvelle Commande
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Patient: {patient?.fullName || patient?.name} ({patient?.patient_code})
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Alert */}
          {error && (
            <div
              role="alert"
              className="rounded-xl border-2 border-red-400 bg-red-50 px-4 py-4 flex items-start gap-3 shadow-sm"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100">
                <AlertCircle className="h-5 w-5 text-red-600" aria-hidden />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-semibold text-red-800">Erreur</p>
                <p className="mt-1.5 text-sm leading-relaxed text-red-700">{error}</p>
              </div>
            </div>
          )}

          {patient && !patientAllowsNewCase(patient) && (
            <div className="bg-muted border border-border rounded-lg p-4 text-sm text-muted-foreground">
              Ce patient est inactif. La création d&apos;une nouvelle conception n&apos;est pas possible tant
              que le dossier n&apos;est pas réactivé.
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              fullWidth
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={isSubmitting || !patientAllowsNewCase(patient)}
              fullWidth
            >
              {isSubmitting ? 'Création en cours...' : 'Créer la commande'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCaseModal;